import {
  PerformanceEventName,
  PerformanceResourceTimingWithServerTiming,
  ResourceEntryType,
} from '@ms/yammer-telemetry/dist/performance';

import { PerformanceEvent } from '../events';
import { TelemetryEventType } from '../events/types';

import { getOperationNameFromServerTiming, getRequestIdFromServerTiming, getResourceEntryType } from './resourceParser';

const normalizeProperties = (properties: Record<string, string | number>): Record<string, string | number> =>
  Object.keys(properties).reduce((rounded: Record<string, string | number>, key) => {
    const value = properties[key];
    if (typeof value === 'number') {
      rounded[key] = Math.round(value);
    }

    return rounded;
  }, properties);

const getJavaScriptResourceProperties = ({ name }: PerformanceResourceTiming) => ({
  bundleName: name,
});

const getGraphqlResourceProperties = (resourceEntry: PerformanceResourceTimingWithServerTiming) => {
  const { serverTiming } = resourceEntry;
  if (!serverTiming) {
    return {};
  }

  const operationName = getOperationNameFromServerTiming(serverTiming);
  const requestId = getRequestIdFromServerTiming(serverTiming);

  return { operationName, requestId };
};

const getFlightingResourceProperties = (resourceEntry: PerformanceResourceTiming) => {
  const nameWithoutQueryParams = resourceEntry.name.substring(0, resourceEntry.name.indexOf('?'));

  return { name: nameWithoutQueryParams, apiName: 'GetEcsFeatureFlags' };
};

const getResourceSpecificProperties = (resourceEntry: PerformanceResourceTiming) => {
  const resourceType = getResourceEntryType(resourceEntry);

  switch (resourceType) {
    case ResourceEntryType.GraphQL:
      return getGraphqlResourceProperties(resourceEntry as PerformanceResourceTimingWithServerTiming);
    case ResourceEntryType.JavaScript:
      return getJavaScriptResourceProperties(resourceEntry as PerformanceResourceTiming);
    case ResourceEntryType.Flighting:
      return getFlightingResourceProperties(resourceEntry as PerformanceResourceTiming);
    default:
      return {};
  }
};

const getResourceTimingProperties = (resourceEntry: PerformanceResourceTimingWithServerTiming) => {
  const {
    name,
    initiatorType,
    nextHopProtocol,
    workerStart,
    redirectStart,
    redirectEnd,
    fetchStart,
    domainLookupStart,
    domainLookupEnd,
    connectStart,
    connectEnd,
    secureConnectionStart,
    requestStart,
    responseStart,
    responseEnd,
    encodedBodySize,
    decodedBodySize,
  } = resourceEntry;
  const properties = normalizeProperties({
    name,
    initiatorType,
    nextHopProtocol,
    workerStart,
    redirectStart,
    redirectEnd,
    fetchStart,
    domainLookupStart,
    domainLookupEnd,
    connectStart,
    connectEnd,
    secureConnectionStart,
    requestStart,
    responseStart,
    responseEnd,
    encodedBodySize,
    decodedBodySize,
  });

  return {
    ...properties,
    ...getResourceSpecificProperties(resourceEntry as PerformanceResourceTiming),
  };
};

type GetResourcePerformanceEvent = (resourceEntry: PerformanceResourceTimingWithServerTiming) => PerformanceEvent;
export const getResourcePerformanceEvent: GetResourcePerformanceEvent = (resourceEntry) => ({
  type: TelemetryEventType.Performance,
  name: PerformanceEventName.ResourceTiming,
  duration: resourceEntry.duration,
  startTime: resourceEntry.startTime,
  occurredAt: Date.now().toString(),
  properties: getResourceTimingProperties(resourceEntry),
});
